import React, { useState, useRef, useEffect } from "react";
import { API, useLanguage } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Send,
  RotateCcw,
  Bot,
  User,
  Sparkles,
  Brain,
  MessageSquare,
  Mic,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const INTENT_LABELS = {
  booking: "Prenotazione",
  modify_booking: "Modifica",
  cancel_booking: "Annullamento",
  check_booking: "Verifica",
  info_services: "Servizi",
  info_general: "Info",
  greeting: "Saluto",
  confirmation: "Conferma",
  selection: "Selezione",
  new_slot_input: "Nuovo slot",
  other: "Altro",
};

const STATE_LABELS = {
  idle: "In attesa",
  awaiting_confirmation: "Attesa conferma",
  awaiting_new_slot: "Nuova data/ora",
  selecting_appointment: "Scelta appuntamento",
  selecting_to_cancel: "Scelta da annullare",
  selecting_to_modify: "Scelta da modificare",
  awaiting_followup: "Follow-up",
};

const SUGGESTIONS = [
  "Controlla il mio appuntamento",
  "Vorrei annullare la mia prenotazione",
  "Sposta il mio appuntamento a domani alle 15",
  "Quali servizi offrite?",
  "Che orari avete?",
];

const AiCustomerCareSimulator = () => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `ai_${Date.now()}`);
  const [lastMeta, setLastMeta] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const toSend = (text ?? input).trim();
    if (!toSend || sending) return;

    setMessages((m) => [...m, { role: "user", text: toSend, ts: new Date() }]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(
        `${API}/wa/simulator/ai-send`,
        { message: toSend, session_id: sessionId, source: "text" },
        { withCredentials: true }
      );
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          text: res.data.response,
          ts: new Date(),
          meta: {
            state: res.data.state,
            last_intent: res.data.last_intent,
            pending_confirmation: res.data.pending_confirmation,
          },
        },
      ]);
      setLastMeta({
        state: res.data.state,
        last_intent: res.data.last_intent,
        pending_confirmation: res.data.pending_confirmation,
      });
    } catch (err) {
      console.error(err);
      toast.error(
        language === "it"
          ? "Errore durante l'invio. Riprova."
          : "Error while sending. Retry."
      );
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    try {
      await axios.post(
        `${API}/wa/simulator/reset`,
        { message: "", session_id: sessionId },
        { withCredentials: true }
      );
      setMessages([]);
      setLastMeta(null);
      toast.success(language === "it" ? "Conversazione resettata" : "Conversation reset");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ai-customer-care-simulator">
      {/* Left: chat */}
      <div className="lg:col-span-2 bg-card border border-border rounded-2xl overflow-hidden flex flex-col" style={{ minHeight: "520px" }}>
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5" />
            <div>
              <p className="font-semibold text-sm">AI Customer Care</p>
              <p className="text-xs text-violet-100 opacity-90">gpt-4o-mini · Whisper · Confirmation Safe</p>
            </div>
          </div>
          <Button
            onClick={reset}
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/20 rounded-full"
            data-testid="ai-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            {language === "it" ? "Reset" : "Reset"}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
          {messages.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-10">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-violet-400 opacity-60" />
              <p>
                {language === "it"
                  ? "Prova a scrivere una richiesta naturale — il bot capirà."
                  : "Try a natural sentence — the bot will understand."}
              </p>
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`ai-msg-${i}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-emerald-500 text-white rounded-br-sm"
                    : "bg-card border border-border text-foreground rounded-bl-sm"
                }`}
              >
                <p className="whitespace-pre-wrap">{m.text}</p>
                {m.meta?.last_intent && (
                  <p className="text-[10px] mt-1 opacity-70">
                    {INTENT_LABELS[m.meta.last_intent] || m.meta.last_intent}
                  </p>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-card border border-border rounded-2xl px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                {language === "it" ? "sta scrivendo…" : "typing…"}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="px-3 py-3 border-t border-border flex items-center gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={language === "it" ? "Scrivi un messaggio…" : "Type a message…"}
            disabled={sending}
            data-testid="ai-input"
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            className="rounded-full"
            data-testid="ai-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>

      {/* Right: suggestions + state */}
      <div className="space-y-4">
        <div className="bg-card border border-border rounded-2xl p-4" data-testid="ai-suggestions">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-violet-500" />
            {language === "it" ? "Prova con…" : "Try…"}
          </h3>
          <div className="space-y-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                disabled={sending}
                className="w-full text-left px-3 py-2 rounded-lg bg-muted/60 hover:bg-muted text-sm text-foreground transition-colors"
                data-testid={`ai-suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-4" data-testid="ai-meta-panel">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            {language === "it" ? "Stato conversazione" : "Conversation state"}
          </h3>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Stato:</span>
              <span className="font-medium text-foreground">
                {STATE_LABELS[lastMeta?.state] || lastMeta?.state || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Intent:</span>
              <span className="font-medium text-foreground">
                {INTENT_LABELS[lastMeta?.last_intent] || lastMeta?.last_intent || "-"}
              </span>
            </div>
            {lastMeta?.pending_confirmation && (
              <div className="mt-2 p-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                  <Mic className="w-3 h-3 inline mr-1" />
                  {language === "it" ? "Conferma in attesa" : "Pending confirmation"}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  {lastMeta.pending_confirmation.action}: {lastMeta.pending_confirmation.summary}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-200 dark:border-violet-900 rounded-2xl p-4 text-xs text-muted-foreground">
          <p className="font-medium text-foreground mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-violet-500" />
            {language === "it" ? "Come funziona" : "How it works"}
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>{language === "it" ? "Audio WhatsApp → Whisper" : "WhatsApp audio → Whisper"}</li>
            <li>{language === "it" ? "gpt-4o-mini rileva intent + entità" : "gpt-4o-mini extracts intent + entities"}</li>
            <li>{language === "it" ? "Conferma sempre prima di cancellare" : "Always confirms before cancel"}</li>
            <li>{language === "it" ? "Memoria per utente persistente" : "Per-user persistent memory"}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AiCustomerCareSimulator;
